/**
 * Card-launcher model for BrandTheme sidebar layout `cards`.
 * One ungrouped grid of permissioned sidebar links, ordered by daily use.
 */

import { canAction, getSidebarNavSections } from '@/composables/usePermissions'

export const SIDEBAR_CARD_TONES = {
  green: { bg: '#E8F8EF', fg: '#16A34A' },
  blue: { bg: '#E8F1FF', fg: '#2563EB' },
  navy: { bg: '#EEF2FF', fg: '#1E3A8A' },
  orange: { bg: '#FFF4E5', fg: '#EA580C' },
}

const TONE_ORDER = ['green', 'blue', 'navy', 'orange']

/** Highest daily-use tools first. Unknown paths keep sidebar order after these. */
export const CARD_PRIORITY = [
  '/manage/inbox',
  '/manage/contacts',
  '/manage/templates',
  '/manage/templates?tab=broadcasts',
  '/dashboard/orders',
  '/dashboard/products',
  '/dashboard',
  '/manage/clients',
  '/manage/credit-monitor',
  '/manage/withdrawals',
  '/manage/alerts',
  '/manage/tickets',
  '/manage/promotions',
  '/manage/company-wabas',
  '/manage/listing-management',
  '/manage/categories',
  '/manage/store-wallet',
  '/manage/wallets',
  '/manage/debit-orders',
  '/manage/payment-proofs',
  '/manage/settlements',
  '/manage/packages',
  '/manage/billing-rates',
  '/manage/reporting',
  '/manage/xero-accounting',
  '/manage/appearance',
  '/manage/subscription',
  '/manage/settlement',
  '/manage/system',
  '/onboarding',
  '/user/my-businesses',
  '/help',
]

const TEMPLATES_PATH = '/manage/templates'
const BROADCASTS_PATH = '/manage/templates?tab=broadcasts'
const TEMPLATES_CARD = {
  path: TEMPLATES_PATH,
  label: 'Templates',
  icon: 'fas fa-file-alt',
}
const BROADCASTS_CARD = {
  path: BROADCASTS_PATH,
  label: 'Campaigns',
  icon: 'fas fa-bullhorn',
}

const CARD_BLURB = {
  '/manage/inbox': 'Shared WhatsApp conversations',
  '/manage/contacts': 'Team Inbox customers, charts and campaigns',
  [TEMPLATES_PATH]: 'Approved WhatsApp templates',
  [BROADCASTS_PATH]: 'Send template campaigns to customers',
  '/dashboard/orders': 'Track, fulfil and refund customer orders',
  '/dashboard/products': 'Catalogue, stock and pricing',
  '/dashboard': 'Overview and activity',
  '/manage/clients': 'Client platforms and their admins',
  '/manage/credit-monitor': 'Credit wallet · AI and WhatsApp',
  '/manage/withdrawals': 'Review and approve payout requests',
  '/manage/alerts': 'Store management centre',
  '/manage/tickets': 'Support requests from stores',
  '/manage/promotions': 'Discounts, campaigns and broadcasts',
  '/manage/company-wabas': 'Number, templates and connection',
  '/manage/listing-management': 'Directory listings and visibility',
  '/manage/categories': 'Organise what stores can sell',
  '/manage/store-wallet': 'Store balances and payouts',
  '/manage/wallets': 'Balances and broadcast usage',
  '/manage/debit-orders': 'Package and wallet debit runs',
  '/manage/payment-proofs': 'EFT proofs waiting for approval',
  '/manage/settlements': 'Platform commerce overview',
  '/manage/packages': 'SaaS plans and catalogue pricing',
  '/manage/billing-rates': 'WhatsApp and AI billing rates',
  '/manage/reporting': 'Ask questions, get reports',
  '/manage/xero-accounting': 'Ledger batches and accounting',
  '/manage/appearance': 'Logo, colours and chrome',
  '/manage/subscription': 'Seats, stores and billing',
  '/manage/settlement': 'My Platform payouts to Clinch',
  '/manage/system': 'Users, roles and platform tools',
  '/onboarding': 'Finish My Platform setup',
  '/user/my-businesses': 'Stores you own',
  '/help': 'Guides for everything your role can do',
}

const PRIORITY_RANK = new Map(CARD_PRIORITY.map((path, index) => [path, index]))

export function canOpenTemplatesCard(user) {
  if (!user) return false
  if (user.is_superuser) return true
  const perms = user.permissions || []
  return (
    canAction(perms, 'team_inbox', 'templates', 'read') ||
    canAction(perms, 'team_inbox', 'broadcasts', 'read')
  )
}

export function canOpenBroadcastsCard(user) {
  if (!user) return false
  if (user.is_superuser) return true
  const perms = user.permissions || []
  return canAction(perms, 'team_inbox', 'broadcasts', 'read')
}

function extraCardItems(user) {
  const extra = []
  if (canOpenTemplatesCard(user)) extra.push(TEMPLATES_CARD)
  if (canOpenBroadcastsCard(user)) extra.push(BROADCASTS_CARD)
  return extra
}

function flattenNavItems(user) {
  const { rows } = getSidebarNavSections(user)
  const items = []
  const seen = new Set()
  for (const row of rows || []) {
    if (row.type === 'group') {
      for (const item of row.group?.items || []) {
        if (!item?.path || seen.has(item.path)) continue
        seen.add(item.path)
        items.push(item)
      }
      continue
    }
    const item = row.item
    if (!item?.path || seen.has(item.path)) continue
    seen.add(item.path)
    items.push(item)
  }
  return items
}

function toneForIndex(index) {
  return TONE_ORDER[index % TONE_ORDER.length]
}

function withTones(cards) {
  return cards.map((card, index) => {
    const tone = card.tone || toneForIndex(index)
    return {
      ...card,
      tone,
      toneColors: SIDEBAR_CARD_TONES[tone],
    }
  })
}

function byDailyUse(a, b) {
  const fallback = CARD_PRIORITY.length
  const rankA = PRIORITY_RANK.has(a.path) ? PRIORITY_RANK.get(a.path) : fallback
  const rankB = PRIORITY_RANK.has(b.path) ? PRIORITY_RANK.get(b.path) : fallback
  return rankA - rankB
}

/**
 * Permission-filtered Apps cards, ordered by daily use (no section headings).
 */
export function getSidebarNavCards(user) {
  const items = flattenNavItems(user)
  const seen = new Set(items.map((item) => item.path))
  for (const extra of extraCardItems(user)) {
    if (seen.has(extra.path)) continue
    seen.add(extra.path)
    items.push(extra)
  }
  const cards = items
    .sort(byDailyUse)
    .map((item) => ({
      path: item.path,
      label: item.label,
      icon: item.icon || 'fas fa-th-large',
      exact: Boolean(item.exact),
      blurb: CARD_BLURB[item.path] || 'Open this workspace tool',
    }))
  return withTones(cards)
}

/** @deprecated Use getSidebarNavCards. Kept so older imports still resolve. */
export function getSidebarCardSections(user) {
  const cards = getSidebarNavCards(user)
  return cards.length ? [{ id: 'apps', label: '', cards }] : []
}

/** Compact demo cards for Appearance live preview (not permission-filtered). */
export function getPreviewSidebarNavCards() {
  return withTones([
    { label: 'Team Inbox', icon: 'fab fa-whatsapp', blurb: 'Shared WhatsApp conversations', tone: 'green' },
    { label: 'Contacts', icon: 'fas fa-address-book', blurb: 'Team Inbox customers, charts and campaigns', tone: 'blue' },
    { label: 'Templates', icon: 'fas fa-file-alt', blurb: 'Approved WhatsApp templates', tone: 'navy' },
    { label: 'Campaigns', icon: 'fas fa-bullhorn', blurb: 'Send template campaigns to customers', tone: 'orange' },
    { label: 'Orders', icon: 'fas fa-shopping-cart', blurb: 'Track, fulfil and refund customer orders', tone: 'orange' },
    { label: 'Products', icon: 'fas fa-box', blurb: 'Catalogue, stock and pricing', tone: 'green' },
    { label: 'Dashboard', icon: 'fas fa-home', blurb: 'Overview and activity', tone: 'blue' },
    { label: 'Client platforms', icon: 'fas fa-globe', blurb: 'Client platforms and their admins', tone: 'blue' },
    { label: 'Credit monitoring', icon: 'fas fa-gauge-high', blurb: 'Credit wallet · AI and WhatsApp', tone: 'navy' },
    { label: 'Withdrawals', icon: 'fas fa-money-check-alt', blurb: 'Review and approve payout requests', tone: 'orange' },
    { label: 'SMC', icon: 'fas fa-warehouse', blurb: 'Store management centre', tone: 'green', badge: '22' },
  ])
}

export function getPreviewSidebarCardSections() {
  return [{ id: 'apps', label: '', cards: getPreviewSidebarNavCards() }]
}
